import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowRight, Eye, EyeOff } from 'lucide-react';
import { getDashboardUrl } from '../../lib/domains';

const COUNTRIES = [
  'Uganda', 'Kenya', 'Tanzania', 'Rwanda', 'Burundi',
  'Nigeria', 'Ghana', 'South Africa', 'Zambia', 'Malawi',
  'Cameroon', 'Senegal', 'Ivory Coast',
  'United States', 'United Kingdom', 'Canada',
  'France', 'Germany', 'Netherlands', 'Belgium', 'Italy', 'Spain',
  'Other',
];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '', country: 'Uganda', account_type: 'individual', business_name: '' });
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    const result = await register(form);
    setLoading(false);
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
  };

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  return (
    <div className="min-h-screen flex bg-slate-50">
      <div className="hidden lg:flex lg:w-1/2 bg-emerald-700 text-white flex-col justify-between p-12">
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Jjuma Pay</h1>
          <p className="text-emerald-200 mt-1 text-sm">Payment Gateway for East Africa</p>
        </div>
        <div>
          <h2 className="text-2xl font-semibold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Start receiving payments today
          </h2>
          <p className="text-emerald-100 leading-relaxed max-w-md">
            Perfect for social media creators, freelancers, churches, NGOs, and small businesses.
            Create payment links and share with your audience.
          </p>
        </div>
        <p className="text-emerald-300 text-xs">Join thousands of creators in East Africa</p>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <h1 className="text-2xl font-bold text-emerald-700" style={{ fontFamily: 'Outfit, sans-serif' }}>Jjuma Pay</h1>
          </div>
          <h2 className="text-2xl font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Create your account
          </h2>
          <p className="text-slate-500 text-sm mb-6">Get started with Jjuma Pay in minutes</p>

          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="register-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-slate-700">Full Name</Label>
              <Input
                id="name"
                data-testid="register-name-input"
                value={form.name}
                onChange={e => update('name', e.target.value)}
                placeholder="Your full name"
                className="mt-1.5"
                required
              />
            </div>
            <div>
              <Label htmlFor="email" className="text-slate-700">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="register-email-input"
                value={form.email}
                onChange={e => update('email', e.target.value)}
                placeholder="you@example.com"
                className="mt-1.5"
                required
              />
            </div>
            <div>
              <Label htmlFor="phone" className="text-slate-700">Phone Number</Label>
              <Input
                id="phone"
                data-testid="register-phone-input"
                value={form.phone}
                onChange={e => update('phone', e.target.value)}
                placeholder="+256..."
                className="mt-1.5"
              />
            </div>
            <div>
              <Label className="text-slate-700">Country</Label>
              <Select value={form.country} onValueChange={v => update('country', v)}>
                <SelectTrigger className="mt-1.5" data-testid="register-country-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-700">Account Type</Label>
              <Select value={form.account_type} onValueChange={v => update('account_type', v)}>
                <SelectTrigger className="mt-1.5" data-testid="register-account-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="individual">Individual</SelectItem>
                  <SelectItem value="business">Business / Organisation</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.account_type === 'business' && (
              <div>
                <Label className="text-slate-700">Business / Organisation Name</Label>
                <Input
                  data-testid="register-business-name-input"
                  value={form.business_name}
                  onChange={e => update('business_name', e.target.value)}
                  placeholder="Your business name"
                  className="mt-1.5"
                  required
                />
              </div>
            )}
            <div>
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <div className="relative mt-1.5">
                <Input
                  id="password"
                  type={showPw ? 'text' : 'password'}
                  data-testid="register-password-input"
                  value={form.password}
                  onChange={e => update('password', e.target.value)}
                  placeholder="At least 6 characters"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              data-testid="register-submit-btn"
              disabled={loading}
              className="w-full bg-emerald-700 hover:bg-emerald-800 text-white"
            >
              {loading ? 'Creating account...' : 'Create Account'}
              {!loading && <ArrowRight size={16} className="ml-2" />}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-500">
            Already have an account?{' '}
            <Link to={getDashboardUrl('/login')} data-testid="login-link" className="text-emerald-700 font-medium hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
